/**
 * Sprint 10 Batch 1.3: Feature Gated Wrapper Component
 * Wraps premium features with upgrade prompts for Trial users
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../context/SubscriptionContext';
import { FEATURE_DEFINITIONS, formatPrice } from '../config/packages';
import { usePackageCatalog } from '../context/PackageCatalogContext';
import { Button } from './ui/button';
import { Lock, ArrowUpCircle, Zap, Sparkles } from 'lucide-react';

/**
 * FeatureGatedWrapper - Wraps content that requires a premium feature
 * Shows upgrade prompt if feature is not available
 * 
 * @param {string} feature - Feature key from packages.js (e.g., 'bulkOperations')
 * @param {React.ReactNode} children - Content to show when feature is available
 * @param {string} variant - 'inline' | 'card' | 'banner' | 'tooltip'
 * @param {string} className - Additional CSS classes
 */
export const FeatureGatedWrapper = ({ 
  feature, 
  children, 
  variant = 'inline',
  className = '',
  fallbackAction = null // Optional callback when upgrade is clicked
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canUseFeature, currentPackage, isActive } = useSubscription();
  const { getUpgradePath } = usePackageCatalog();
  
  const featureInfo = FEATURE_DEFINITIONS[feature] || { name: feature, description: '' };
  const isAvailable = canUseFeature(feature);
  const upgradePath = currentPackage ? getUpgradePath(currentPackage.id) : null;
  
  // Default upgrade action - navigate to clinic packages page
  const handleUpgradeClick = () => {
    if (fallbackAction) {
      fallbackAction();
    } else {
      navigate('/clinic/packages');
    }
  };
  
  // If feature is available, render children directly
  if (isAvailable) {
    return children;
  }
  
  // Feature is locked - render upgrade prompt based on variant
  const upgradeMessage = currentPackage?.upgradePrompt || t('featureGate.upgradeRequired', 'Upgrade to unlock this feature');
  
  // Inline variant - small lock icon with tooltip
  if (variant === 'inline') {
    return (
      <div className={`relative ${className}`}>
        <div className="opacity-40 pointer-events-none select-none blur-[1px]">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-white/80 to-white/95 backdrop-blur-[1px] rounded-lg">
          <div className="text-center px-4 py-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Lock className="h-5 w-5 text-gray-500" />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              {featureInfo.name}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              {t('featureGate.availableIn', { plan: upgradePath?.displayName || 'Starter' })}
            </p>
            <Button 
              size="sm" 
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              onClick={handleUpgradeClick}
            >
              <ArrowUpCircle className="h-4 w-4 mr-1" />
              {t('featureGate.upgrade', 'Upgrade')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Card variant - full card replacement
  if (variant === 'card') {
    return (
      <div className={`border border-dashed border-gray-300 rounded-lg p-6 bg-gray-50/50 ${className}`}>
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-3">
            <Lock className="h-6 w-6 text-gray-400" />
          </div>
          <h4 className="font-semibold text-gray-800 mb-1">
            {featureInfo.name}
          </h4>
          <p className="text-sm text-gray-500 mb-4 max-w-xs">
            {featureInfo.description}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
            <Zap className="h-3 w-3" />
            <span>{t('featureGate.premiumFeature', 'Premium Feature')}</span>
          </div>
          <Button 
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            onClick={handleUpgradeClick}
          >
            <ArrowUpCircle className="h-4 w-4 mr-2" />
            {t('featureGate.upgradeToUnlock', 'Upgrade to Unlock')}
          </Button>
          {upgradePath && (
            <p className="text-xs text-gray-400 mt-2">
              {t('featureGate.startingAt', 'Starting at')} {formatPrice(upgradePath.pricing)}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // Banner variant - horizontal banner
  if (variant === 'banner') {
    return (
      <div className={`flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
            <Lock className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-gray-800">
              {featureInfo.name}
            </p>
            <p className="text-sm text-gray-600">
              {upgradeMessage}
            </p>
          </div>
        </div>
        <Button 
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          onClick={handleUpgradeClick}
        >
          <ArrowUpCircle className="h-4 w-4 mr-2" />
          {t('featureGate.upgrade', 'Upgrade')}
        </Button>
      </div>
    );
  }
  
  // Tooltip variant - shows children but with disabled state
  if (variant === 'tooltip') {
    return (
      <div className={`relative group ${className}`} title={upgradeMessage}>
        <div className="opacity-50 cursor-not-allowed">
          {children}
        </div>
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
          <Lock className="h-3 w-3 inline mr-1" />
          {t('featureGate.upgradeRequired', 'Upgrade required')}
        </div>
      </div>
    );
  }
  
  // Default fallback
  return (
    <div className={`text-center p-4 ${className}`}>
      <Lock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
      <p className="text-sm text-gray-600">{upgradeMessage}</p>
    </div>
  );
};

/**
 * GatedButton - A button that shows locked state when feature unavailable
 */
export const GatedButton = ({
  feature,
  children,
  onClick,
  className = '',
  ...props
}) => {
  const { t } = useTranslation();
  const { canUseFeature, currentPackage } = useSubscription();
  const { getUpgradePath } = usePackageCatalog();
  
  const isAvailable = canUseFeature(feature);
  const featureInfo = FEATURE_DEFINITIONS[feature] || { name: feature };
  const upgradePath = currentPackage ? getUpgradePath(currentPackage.id) : null;
  
  if (isAvailable) {
    return (
      <Button onClick={onClick} className={className} {...props}>
        {children}
      </Button>
    );
  }
  
  // Locked button
  return (
    <div className="relative group">
      <Button 
        disabled 
        className={`opacity-60 cursor-not-allowed ${className}`}
        {...props}
      >
        <Lock className="h-4 w-4 mr-1" />
        {children}
      </Button>
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3 w-3 text-yellow-400" />
          <span>
            {t('featureGate.availableIn', { plan: upgradePath?.displayName || 'Starter' })}
          </span>
        </div>
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};

/**
 * TrialFeatureNotice - Small notice for trial users about feature limitations
 */
export const TrialFeatureNotice = ({ className = '' }) => {
  const { t } = useTranslation();
  const { currentPackage, trialStatus } = useSubscription();
  
  // Only show for trial users
  if (!currentPackage || currentPackage.id !== 'TRIAL') {
    return null;
  }
  
  return (
    <div className={`flex items-center gap-2 p-2 bg-blue-50 border border-blue-100 rounded-lg text-sm ${className}`}>
      <Sparkles className="h-4 w-4 text-blue-500 flex-shrink-0" />
      <span className="text-blue-700">
        {t('featureGate.trialNotice', 'Some features are limited during your trial. Upgrade anytime to unlock everything.')}
      </span>
    </div>
  );
};

export default FeatureGatedWrapper;
